/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {ThunkAction} from 'model/store/thunk'
import {
	Offer,
	OfferAddItemPayload,
	OfferChangeItemPayload,
	OfferChangePaymentPlanPayload,
	type OfferChangePaymentTypePayload,
	OfferCreatePayload,
	OfferCreateReservationResponse,
	OfferDeleteItemPayload,
} from 'types/entities/Offer'
import {RootState} from 'helpers/store'
import {OfferCalculation} from 'types/entities/OfferCalculation'
import {OfferNote} from 'types/entities/OfferNote'
import {
	deleteOffer,
	deleteOfferNote,
	pushOffer,
	pushOfferNote,
	resetSelectedOffer,
    clearSelectedOfferReservation,
	setOffers,
	setPackageCartToOffer,
	setSelectedOffer,
	setSelectedOfferCalculationPackagesCart,
	setSelectedOfferPackagesCart,
} from './OffersReducer'
import RequestAction from 'store/RequestAction'
import {parsePaginatedResponse} from 'utils/api/pagination'
import BookingAction from 'store/booking/BookingAction'
import {PackageCategory} from 'types/enums/PackageCategory'
import {DEFAULT_HALL_ID, SELLER_API_KEY} from 'helpers/config'
import {LeadStatus} from 'types/enums/LeadStatus'
import {ReservationStatus} from 'types/enums/ReservationStatus'
import OpportunitiesAction from 'store/opportunities/OpportunitiesAction'
import ajax from 'helpers/ajax'
import {setPackageCartToReservation} from 'store/reservations/ReservationsReducer'
import ReservationsAction from 'store/reservations/ReservationsAction'
import {PackagesCart} from 'types/entities/PackagesCart'
import {PackagesCartItemVoucher} from 'types/entities/PackagesCartItemVoucher'
import {MassActionRequest, MassActionResponse} from 'types/requests/MassActionRequest'
import {consoleLog} from 'utils/utility'
import {extractReservationInfo, isActiveOfferReservation, sortOfferCalculationsForDisplay} from 'utils/offers/offersUtils'
import { transformOffersSearch } from 'utils/offers/offerTableUtils'
import { formatFilterDate, parseFilterDate } from 'utils/dateRangePresets'

/**
 * Strips all relationship / computed / server-generated fields from an offer
 * payload before sending it to POST /admin/offers/create.
 * Only explicitly allowed scalar fields reach the API.
 */
const ALLOWED_OFFER_FIELDS = new Set<string>([
    'id',
    'seller_id',
    'company_id',
    'lead_id',
    'contact_id',
    'address_id',
    'parent_offer_id',
    'packages_cart_id',
    'input',
    'valid_until',
    'probability',
    'variant_group_guid',
    'is_netto',
    'status',
    'extra',
    // virtual create-only fields
    'contact_person',
    'email',
    'phone',
    'company_name',
])

function sanitizeOfferPayload<T extends Record<string, unknown>>(data: T): Partial<T> {
    const result: Partial<T> = {}
    for (const key of Object.keys(data)) {
        if (ALLOWED_OFFER_FIELDS.has(key) && data[key] !== undefined) {
            ;(result as Record<string, unknown>)[key] = data[key]
        }
    }
    return result
}

export class OffersAction {
    static fetchOffersTablePage = (params: {
        page: number
        per_page: number
        view?: string | 'all'
        search?: any[]
        sorting?: { id: string; desc: boolean }[]
        sort_by?: string | null
        sort?: 'asc' | 'desc' | null
        [key: string]: any
    }): ThunkAction<
        Promise<{
            data: Offer[]
            current_page: number
            per_page: number
            total: number
        }>
    > => {
        return async (
            dispatch,
            getState
        ): Promise<{
            data: Offer[]
            current_page: number
            per_page: number
            total: number
        }> => {
            const url = `/admin/offers/table`

            let searchParams: any[] = params.search

            if (Array.isArray(searchParams)) {
                searchParams = transformOffersSearch(searchParams)

                const specialFilters = ['created_at', 'updated_at', 'valid_until']
                const rangeFilters = ['persons', 'event_time', 'value'] // Range filters for BE validation

                searchParams = searchParams.map((f: any) => {
                    // Handle date range filters
                    if (specialFilters.includes(f.id)) {
                        if (f.value && typeof f.value === 'object' && 'from' in f.value && 'to' in f.value) {
                            const fromDate = parseFilterDate(String(f.value.from ?? ''))
                            const toDate = parseFilterDate(String(f.value.to ?? ''))
                            if (!fromDate || !toDate) {
                                return f
                            }
                            return {
                                ...f,
                                value: {
                                    from: formatFilterDate(fromDate),
                                    to: formatFilterDate(toDate),
                                },
                            }
                        }
                    }

                    // Handle time range filters - ensure proper format for backend
                    if (f.id === 'event_time' && f.value && typeof f.value === 'object') {
                        // Validate and normalize time format (HH:MM)
                        return {
                            ...f,
                            value: {
                                from: f.value.from || null,
                                to: f.value.to || null,
                            },
                        }
                    }

                    // Handle number range filters (persons, revenue)
                    if (rangeFilters.includes(f.id) && f.value && typeof f.value === 'object') {
                        const parseRangeValue =
                            f.id === 'value'
                                ? (raw: unknown) => {
                                      if (raw === undefined || raw === null || raw === '') {
                                          return null
                                      }

                                      const parsed = parseFloat(String(raw))
                                      return Number.isFinite(parsed) ? parsed : null
                                  }
                                : (raw: unknown) => {
                                      if (raw === undefined || raw === null || raw === '') {
                                          return null
                                      }

                                      const parsed = parseInt(String(raw), 10)
                                      return Number.isFinite(parsed) ? parsed : null
                                  }

                        return {
                            ...f,
                            value: {
                                from: parseRangeValue(f.value.from),
                                to: parseRangeValue(f.value.to),
                            },
                        }
                    }

                    return f
                })
            }

            const response: unknown = await RequestAction.makeAuthenticatedRequest(
                getState,
                url,
                {
                    ...params,
                    view: params.view && params.view !== 'all' ? params.view : undefined,
                    search: searchParams,
                    sorting: params.sorting && params.sorting.length > 0 ? params.sorting : undefined,
                },
                'post'
            )
            try {
                const parsedResponse = parsePaginatedResponse<Offer>(response, 'offers')

                dispatch(setOffers(parsedResponse.data))

                return parsedResponse
            } catch {
                return Promise.reject(response)
            }
        }
    }

    static fetchExportOffersXlsx = (params: {
        view?: string | 'all'
        search?: unknown
        sorting?: { id: string; desc: boolean }[]
        sort_by?: string | null
        sort?: 'asc' | 'desc' | null
        export?: 'xlsx' | 'csv' | 'pdf'
        [key: string]: any
    }): ThunkAction<Promise<string>> => {
        return async (_dispatch, getState): Promise<string> => {
            const url = `/admin/export/offers`

            const response: { url: string } = await RequestAction.makeAuthenticatedRequest(
                getState,
                url,
                {
                    ...params,
                    page: params.page ?? 1,
                    per_page: params.per_page ?? 50,
                    view: params.view && params.view !== 'all' ? params.view : undefined,
                    search: Array.isArray(params.search)
                        ? transformOffersSearch(params.search as { id: string; value: unknown }[])
                        : params.search,
                    sorting: params.sorting && params.sorting.length > 0 ? params.sorting : undefined,
                    export: params.export ?? 'xlsx',
                },
                'post',
                'json'
            )

            return response.url
        }
    }

    static fetchExportSalesXlsx = (params: {
        view?: string | 'all'
        search?: unknown
        sorting?: { id: string; desc: boolean }[]
        sort_by?: string | null
        sort?: 'asc' | 'desc' | null
        [key: string]: any
    }): ThunkAction<Promise<Blob>> => {
        return async (_dispatch, getState): Promise<Blob> => {
            const url = `/admin/export/sales`

            const response: Blob = await RequestAction.makeAuthenticatedRequest(
                getState,
                url,
                {
                    ...params,
                    page: params.page ?? 1,
                    per_page: params.per_page ?? 50,
                    view: params.view && params.view !== 'all' ? params.view : undefined,
                    search: Array.isArray(params.search)
                        ? transformOffersSearch(params.search as { id: string; value: unknown }[])
                        : params.search,
                    sorting: params.sorting && params.sorting.length > 0 ? params.sorting : undefined,
                    export: 'xlsx',
                },
                'post',
                'blob'
            )

            return response
        }
    }

    static fetchExportOfferPdf = (
        id: number,
        isCalculation: boolean = false,
        netto_pdf: boolean = true
    ): ThunkAction<Promise<{ offer: string; download_as: string }>> => {
        return async (_dispatch, getState): Promise<{ offer: string; download_as: string }> => {
            const url = isCalculation
                ? `/admin/offers/export-pdf/calculation/${id}?netto_pdf=${netto_pdf}`
                : `/admin/offers/export-pdf/offer/${id}?netto_pdf=${netto_pdf}`
            const response: { offer: string; download_as: string } = await RequestAction.makeAuthenticatedRequest(
                getState,
                url,
                undefined,
                'get'
            )
            return response
        }
    }

    static fetchExportOfferConfirmationPdf = (
        calculation_id: number,
    ): ThunkAction<Promise<{ pdf: string; download_as: string }>> => {
        return async (_dispatch, getState): Promise<{ pdf: string; download_as: string }> => {
            const url =  `/admin/offers/export-pdf/confirmation/${calculation_id}`

            return await RequestAction.makeAuthenticatedRequest(getState, url, undefined, 'get')
        }
    }

    static fetchCreateOrUpdateOffer = (
        data: (Partial<Offer> | OfferCreatePayload) & { id?: number }
    ): ThunkAction<Promise<Offer>> => {
        return async (dispatch, getState): Promise<Offer> => {
            const response: { offer: Offer } = await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/offers/create',
                sanitizeOfferPayload(data as Record<string, unknown>)
            )

            if (response) {
                dispatch(pushOffer(response.offer))
                dispatch(setSelectedOffer(response.offer))
                return response.offer
            }
            return Promise.reject(response)
        }
    }

    static updateOffer = (data: Partial<Offer> & { id: number }): ThunkAction<Promise<Offer>> => {
        return async (dispatch, getState): Promise<Offer> => {
            const response: { offer: Offer } = await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/offers/create',
                sanitizeOfferPayload(data as Record<string, unknown>)
            )
            if (response?.offer) {
                dispatch(pushOffer(response.offer))
                dispatch(setSelectedOffer(response.offer))
                return response.offer
            }
            return Promise.reject(response)
        }
    }

    static fetchMassAction = (
        payload: MassActionRequest
    ): ThunkAction<Promise<MassActionResponse>> => {
        return async (_dispatch, getState): Promise<MassActionResponse> => {
            return await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/offers/mass-actions',
                payload,
                'post'
            )
        }
    }

    static duplicateOffer = (offer: Offer): ThunkAction<Promise<Offer>> => {
        return async (dispatch, getState): Promise<Offer> => {
            const state = getState()
            const sellerState = state as unknown as {
                sessionState?: { session?: { uid?: number } }
            }
            const seller_id = sellerState?.sessionState?.session?.uid

            const getGuid = () =>
                (globalThis as { crypto?: { randomUUID?: () => string } })?.crypto?.randomUUID?.() ??
                `${Date.now()}-${Math.random().toString(16).slice(2)}`

            const groupGuid = offer.variant_group_guid ?? getGuid()

            const response: { offer: Offer } = await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/offers/create',
                {
                    seller_id,
                    company_id: offer.company_id,
                    lead_id: offer.lead_id ?? undefined,
                    probability: offer.probability ?? 50,
                    variant_group_guid: groupGuid,
                    parent_offer_id: offer.id,
                }
            )

            if (response?.offer) {
                dispatch(pushOffer(response.offer))
                return response.offer
            }
            return Promise.reject(response)
        }
    }

    static duplicateOfferById = (payload: {
        offerId: number
        count: number
        date?: string
        time?: string
        company_id?: number | null
        package_category_name?: string | null
        calculations?: number[]
        lead_id?: number | null
        address_id?: number | null
        contact_id?: number | null
    }): ThunkAction<Promise<{ offers: Offer[] }>> => {
        return async (dispatch, getState): Promise<{ offers: Offer[] }> => {
            const { offerId, ...data } = payload
            const response: { offers?: Offer[]; offer?: Offer } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/offers/duplicate/${offerId}`,
                data,
                'post'
            )

            const createdOffers: Offer[] =
                response?.offers && response.offers.length > 0
                    ? response.offers
                    : response?.offer
                      ? [response.offer]
                      : []

            if (createdOffers.length > 0) {
                createdOffers.forEach((created) => dispatch(pushOffer(created)))
                return { offers: createdOffers }
            }
            return Promise.reject(response)
        }
    }

    static fetchUpdateOfferItem = (data: OfferChangeItemPayload): ThunkAction<Promise<void>> => {
        return async (dispatch, getState): Promise<void> => {
            const { offer_id, ...payload } = data
            const response: { packagesCart: PackagesCart, success:boolean } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/offers/change-item/${offer_id}`,
                payload,
                'post'
            )

			if (response.packagesCart) {
				const state = getState() as RootState
				const currentOffer: Offer | null = state.offersState.selectedOffer

				if (currentOffer && currentOffer.id === offer_id) {
					dispatch(setSelectedOfferPackagesCart(response.packagesCart))
					dispatch(setSelectedOfferCalculationPackagesCart(response.packagesCart))
					return Promise.resolve();
				}
			}
            return Promise.reject(response)
        }
    }

    static fetchChangeOfferPaymentPlan = (data: OfferChangePaymentPlanPayload): ThunkAction<Promise<Offer>> => {
        return async (dispatch, getState): Promise<Offer> => {
            const response: { offer: Offer } = await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/offers/change-payment-plan',
                data,
                'post'
            )

            if (response?.offer) {
                dispatch(pushOffer(response.offer))
                dispatch(setSelectedOffer(response.offer))
                return response.offer
            }
            return Promise.reject(response)
        }
    }


	static fetchChangeOfferPaymentType = (data: OfferChangePaymentTypePayload): ThunkAction<Promise<Offer>> => {
        return async (dispatch, getState): Promise<Offer> => {
            const response: { offer: Offer } = await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/offers/change-payment-type/' + data.offer_id,
                data,
                'post'
            )

            if (response?.offer) {
                dispatch(pushOffer(response.offer))
                dispatch(setSelectedOffer(response.offer))
                return response.offer
            }
            return Promise.reject(response)
        }
    }

    static fetchAddOfferItem = (data: OfferAddItemPayload): ThunkAction<Promise<void>> => {
        return async (dispatch, getState): Promise<void> => {
            const { offer_id, ...payload } = data
            const response: { packagesCart: PackagesCart } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/offers/add-item/${offer_id}`,
                payload,
                'post'
            )

            if (response.packagesCart) {
                const state = getState() as RootState
                const currentOffer: Offer | null = state.offersState.selectedOffer

                if (currentOffer && currentOffer.id === offer_id) {
                    dispatch(setSelectedOfferPackagesCart(response.packagesCart))
                    dispatch(setSelectedOfferCalculationPackagesCart(response.packagesCart))
					return Promise.resolve();
                }
            }

            return Promise.reject(response)
        }
    }

    static fetchDeleteOfferItem = (data: OfferDeleteItemPayload): ThunkAction<Promise<void>> => {
        return async (dispatch, getState): Promise<void> => {
            const { offer_id, ...payload } = data
            const response: {packagesCart: PackagesCart, success:boolean} = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/offers/delete-item/${offer_id}`,
                payload,
                'delete'
            )


			if (response.packagesCart) {
				const state = getState() as RootState
				const currentOffer: Offer | null = state.offersState.selectedOffer

				if (currentOffer && currentOffer.id === offer_id) {
					dispatch(setSelectedOfferPackagesCart(response.packagesCart))
					dispatch(setSelectedOfferCalculationPackagesCart(response.packagesCart))
					return Promise.resolve();
				}
			}

            return Promise.reject(response)
        }
    }

    static fetchPackagesForCalculation = (
        calculation: OfferCalculation,
        category: PackageCategory,
        seller_id: number
    ): ThunkAction<Promise<void>> => {
        return async (dispatch): Promise<void> => {
            if (!calculation.input) {
                // return Promise.reject('Calculation has no input data')
            }

            const input = calculation.input
			const date = input?.date || calculation.date;

            await (dispatch as any)(
                BookingAction.fetchPackagesByCategoryAndTimeSlotForSeller(
                    {
                        adults: calculation.packagesCart.drivers_count_adult || 0,
                        children: calculation.packagesCart.drivers_count_child || 0,
                        date: date || '',
                        package_category_name: category,
                        hall_id: DEFAULT_HALL_ID,
                        activity_ids: [1],
                        timeslot_id: null,
                    },
                    seller_id
                )
            )
        }
    }

    static fetchOfferById = (offerId: number): ThunkAction<Promise<Offer>> => {
        return async (dispatch, getState): Promise<Offer> => {
            const response: { offer: Offer } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/offers/detail/${offerId}`,
                undefined,
                'get'
            )
            if (response?.offer) {
                dispatch(setSelectedOffer(response.offer))
                return response.offer
            }
            return Promise.reject(response)
        }
    }

    static fetchOfferByIdRaw = (offerId: number): ThunkAction<Promise<Offer>> => {
        return async (_dispatch, getState): Promise<Offer> => {
            const response: { offer: Offer } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/offers/detail/${offerId}`,
                undefined,
                'get'
            )

            if (response?.offer) {
                return response.offer
            }

            return Promise.reject(response)
        }
    }

    static fetchDeleteOffer = (offerId: number): ThunkAction<Promise<void>> => {
        return async (dispatch, getState): Promise<void> => {
            const response: { success: boolean } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/offers/delete/${offerId}`,
                undefined,
                'delete'
            )
            if (response?.success) {
                dispatch(deleteOffer(offerId))
                return Promise.resolve()
            }
            return Promise.reject(response)
        }
    }

    static fetchDeleteOfferCalculation = (calculationId: number): ThunkAction<Promise<Offer>> => {
        return async (dispatch, getState): Promise<Offer> => {
            const response: { offer: Offer } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/offers/delete-calculation/${calculationId}`,
                undefined,
                'delete'
            )
            if (response?.offer) {
                // Delete payload often omits soft-deleted calculations; detail includes them
                // (same as a page refresh), so the "Deleted" tab updates immediately.
                const offer = await dispatch(this.fetchOfferById(response.offer.id))
                dispatch(pushOffer(offer))
                return await dispatch(
                    this.enforceTargetCalculation(offer, offer.seller_id, calculationId)
                )
            }
            return Promise.reject(response)
        }
    }

    static fetchUndeleteOfferCalculation = (calculationId: number): ThunkAction<Promise<Offer>> => {
        return async (dispatch, getState): Promise<Offer> => {
            const response: { offer: Offer } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/offers/undelete-calculation/${calculationId}`,
                undefined,
                'post'
            )
            if (response?.offer) {
                dispatch(pushOffer(response.offer))
                dispatch(setSelectedOffer(response.offer))
                return await dispatch(this.enforceTargetCalculation(response.offer, response.offer.seller_id))
            }
            return Promise.reject(response)
        }
    }

    static fetchAddOfferNote = (
        offerId: number,
        note: string,
        replied_note_id?: number | null
    ): ThunkAction<Promise<OfferNote>> => {
        return async (dispatch, getState): Promise<OfferNote> => {
            const state = getState()
            const sellerState = state as unknown as {
                sessionState?: { session?: { uid?: number } }
            }
            const creator_id = sellerState?.sessionState?.session?.uid

            const response: {
                offer: Offer
                note: OfferNote
            } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/offers/set-offer-note/${offerId}`,
                { note, creator_id, replied_note_id },
                'post'
            )

            if (response?.note) {
                dispatch(pushOfferNote(response.note))
                return response.note
            }

            if (response?.offer) {
                dispatch(pushOffer(response.offer))
                dispatch(setSelectedOffer(response.offer))

                // Find the note we just added (highest ID)
				// Irrelevant totally
                const newNote = response.offer.offerNotes.reduce((prev, current) =>
                    prev.id > current.id ? prev : current
                )
                return newNote
            }
            return Promise.reject(response)
        }
    }

    static fetchEditOfferNote = (noteId: number, note: string): ThunkAction<Promise<OfferNote>> => {
        return async (dispatch, getState): Promise<OfferNote> => {
            const response: any = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/offers/edit-note/${noteId}`,
                { note },
                'post'
            )

            const updatedNote = response?.data?.note || response?.note

            if (updatedNote) {
                dispatch(pushOfferNote(updatedNote))

                if (response?.data?.offer || response?.offer) {
                    const offer = response?.data?.offer || response?.offer
                    dispatch(pushOffer(offer))
                }

                return updatedNote
            }
            return Promise.reject(response)
        }
    }

    static fetchDeleteOfferNote = (offerId: number, noteId: number): ThunkAction<Promise<void>> => {
        return async (dispatch, getState): Promise<void> => {
            const response: { success: boolean } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/offers/delete-note/${noteId}`,
                undefined,
                'delete'
            )

            if (response?.success) {
                dispatch(deleteOfferNote({ offer_id: offerId, note_id: noteId }))
                return
            }
            return Promise.reject(response)
        }
    }

    static fetchAddOrEditOfferCalculation = (
        calculationId: number,
        offerId: number,
        sellerId: number,
        data: {
            name?: string | null
            is_target?: boolean
            probability?: number
            packages_cart_id: number
            is_netto?: boolean
        }
    ): ThunkAction<Promise<Offer>> => {
        return async (dispatch, getState): Promise<Offer> => {
            const response: {
                offerCalculation: OfferCalculation
            } = await RequestAction.makeAuthenticatedRequest(getState, '/admin/offers/create-calculation', {
                id: calculationId,
                seller_id: sellerId,
                offer_id: offerId,
                ...data,
            })


			//use pushCalculationToOffer here
            if (response?.offerCalculation) {
                const state = getState() as RootState
                const currentOffer = state.offersState?.selectedOffer

                if (currentOffer && currentOffer.id === offerId) {
                    const updatedCalculations = currentOffer.calculations?.map((calc) => {
                        if (calc.id === calculationId) {
                            return {
                                ...calc,
                                ...response.offerCalculation,
                            }
                        }
                        // If we are setting this one as target, unset others
                        if (response.offerCalculation.is_target) {
                            return { ...calc, is_target: false }
                        }
                        return calc
                    })

                    const updatedOffer = {
                        ...currentOffer,
                        calculations: updatedCalculations,
                    }

                    // Also update packagesCart if it matches
                    if (response.offerCalculation.is_target && response.offerCalculation.packagesCart) {
                        updatedOffer.packagesCart = response.offerCalculation.packagesCart
                    }

                    dispatch(pushOffer(updatedOffer))
                    dispatch(setSelectedOffer(updatedOffer))


					//dont do this on frontend, just fetch it from backend
                    // Update opportunity status when calculation is created and offer has lead_id
                    // if (updatedOffer.lead_id && updatedOffer.lead_id > 0) {
                    //     const currentLead = state.leadsState?.selectedLead
					//
                    //     // Only update if lead status is not already "offer" (Quotation)
                    //     if (
                    //         currentLead &&
                    //         currentLead.id === updatedOffer.lead_id &&
                    //         currentLead.status !== LeadStatus.Quotation
                    //     ) {
                    //         try {
                    //             await dispatch(
                    //                 OpportunitiesAction.fetchCreateOrUpdateLead({
                    //                     id: updatedOffer.lead_id,
                    //                     status: LeadStatus.Quotation,
                    //                     name: currentLead.name,
                    //                     hall_id: currentLead.hall_id ?? DEFAULT_HALL_ID,
                    //                     seller_id: currentLead.seller_id ?? Number(state.sessionState.session?.uid),
                    //                 })
                    //             )
                    //         } catch (e) {
                    //             console.error('Failed to update opportunity status to Offer:', e)
                    //         }
                    //     }
                    // }
					//
                    return updatedOffer
                }
            }

            return Promise.reject(response)
        }
    }

    static fetchDuplicateOfferCalculation = (
        calculationId: number,
        offerId: number,
        sellerId: number,
        name?: string
    ): ThunkAction<Promise<OfferCalculation>> => {
        return async (dispatch, getState): Promise<OfferCalculation> => {
            const response: {
                offerCalculation: OfferCalculation
            } = await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/offers/duplicate-calculation',
                {
                    offer_calculation_id: calculationId,
                    seller_id: sellerId,
                    offer_id: offerId,
                    name: name,
                },
                'post'
            )

			//returns the duplicated Calculation already
            const offerCalculation = response?.offerCalculation

            if (offerCalculation) {
                // Refetch the complete offer to ensure all nested data (packagesCartItems, etc.) is properly loaded
                await dispatch(this.fetchOfferById(offerId))

                const state = getState() as RootState
                const currentOffer = state.offersState?.selectedOffer
                if (currentOffer && currentOffer.id === offerId) {
                    try {
                        await dispatch(this.enforceTargetCalculation(currentOffer, sellerId))
                    } catch (e) {
                        console.error('Failed to enforce target calculation', e)
                    }
                }

                return offerCalculation
            }

            return Promise.reject(response)
        }
    }

    static fetchCreateReservationFromOffer = (
        offerId: number,
        calculationId: number,
        sellerId: number
    ): ThunkAction<Promise<OfferCreateReservationResponse>> => {
        return async (dispatch, getState): Promise<OfferCreateReservationResponse> => {
            const response: OfferCreateReservationResponse = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/offers/create-reservation/${offerId}`,
                {
                    calculation_id: calculationId,
                    seller_id: sellerId,
                },
                'post'
            )

            if (response?.offer) {
                const { reservationId } = extractReservationInfo(response)
                const linkedReservation =
                    response.offer.reservation && isActiveOfferReservation(response.offer.reservation)
                        ? response.offer.reservation
                        : reservationId
                          ? ({
                                id: reservationId,
                                status: ReservationStatus.Confirmed,
                            } as Offer['reservation'])
                          : response.offer.reservation ?? null

                const offerWithReservationLink: Offer = {
                    ...response.offer,
                    reservation: linkedReservation,
                    packagesCart: response.offer.packagesCart
                        ? {
                              ...response.offer.packagesCart,
                              reservation_id:
                                  response.offer.packagesCart.reservation_id || reservationId || null,
                          }
                        : response.offer.packagesCart,
                    calculations: response.offer.calculations?.map((calculation) => {
                        if (!calculation.packagesCart) {
                            return calculation
                        }

                        return {
                            ...calculation,
                            packagesCart: {
                                ...calculation.packagesCart,
                                reservation_id:
                                    calculation.packagesCart.reservation_id || reservationId || null,
                            },
                        }
                    }),
                }

                dispatch(pushOffer(offerWithReservationLink))
                dispatch(setSelectedOffer(offerWithReservationLink))

                const offerNote = offerWithReservationLink.extra?.offer_note?.trim()
                if (reservationId && offerNote) {
                    try {
                        await dispatch(ReservationsAction.fetchSetOfferNoteToReservation(reservationId, offerNote))
                    } catch (error) {
                        console.error('Failed to sync offer note to reservation:', error)
                    }
                }

                // Update opportunity status when reservation is created from offer
                if (offerWithReservationLink.lead_id && offerWithReservationLink.reservation) {
                    const state = getState() as RootState
                    const currentLead = state.leadsState?.selectedLead

                    if (currentLead && currentLead.id === offerWithReservationLink.lead_id) {
                        try {
                            await dispatch(
                                OpportunitiesAction.fetchCreateOrUpdateLead({
                                    id: offerWithReservationLink.lead_id,
                                    status: LeadStatus.Acceptance,
                                    name: currentLead.name,
                                    hall_id: currentLead.hall_id ?? DEFAULT_HALL_ID,
                                    seller_id:
                                        currentLead.seller_id ??
                                        Number((getState() as RootState).sessionState.session?.uid),
                                })
                            )
                        } catch (e) {
                            console.error('Failed to update opportunity status to Accepted:', e)
                        }
                    }
                }
            }

            return response
        }
    }

    static enforceTargetCalculation = (
        offer: Offer,
        sellerId: number,
        excludeId?: number
    ): ThunkAction<Promise<Offer>> => {
        return async (dispatch): Promise<Offer> => {
            const activeCalculations = (offer.calculations ?? []).filter((c) => !c.deleted_at)

            if (activeCalculations.length > 0 && !activeCalculations.some((c) => c.is_target)) {
                // Try to promote a different calculation than the one that was just unmarked
                const candidates = excludeId ? activeCalculations.filter((c) => c.id !== excludeId) : activeCalculations

                // If no other candidates, fall back to all active (it will promote the same one, but this is handled by UI disabling it)
                const source = candidates.length > 0 ? candidates : activeCalculations
                const targetCalc = sortOfferCalculationsForDisplay(source)[0]

                return await dispatch(
                    this.fetchAddOrEditOfferCalculation(targetCalc.id, offer.id, sellerId, {
                        is_target: true,
                        probability: 100,
                        packages_cart_id: targetCalc.packages_cart_id,
                    })
                )
            }
            return offer
        }
    }

    static fetchAddVoucherToCalculation = (
        packages_cart_id: number,
        value: number,
        offerId: number,
        sellerId: number
    ): ThunkAction<Promise<Offer>> => {
        return async (dispatch, getState): Promise<Offer> => {
            const response: {
                offer?: Offer
            } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/offers/add-voucher/${packages_cart_id}`,
                {
                    type: 'price',
                    value,
                    offer_id: offerId,
                    seller_id: sellerId,
                }
            )

            if (response?.offer) {
                dispatch(pushOffer(response.offer))
                dispatch(setSelectedOffer(response.offer))
                return response.offer
            }

            // If we got a response but no offer object, fetch the offer to update state
            try {
                return await dispatch(OffersAction.fetchOfferById(offerId))
            } catch {
                // If fetch fails but request succeeded, just resolve with empty object
                return {} as Offer
            }
        }
    }

    static fetchEditVoucherInCalculation = (
        packages_cart_id: number,
        value: number,
        offerId: number,
        sellerId: number,
        voucherId: number
    ): ThunkAction<Promise<Offer>> => {
        return async (dispatch, getState): Promise<Offer> => {
            const response: {
                offer?: Offer
            } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/offers/edit-voucher/${packages_cart_id}`,
                {
                    type: 'price',
                    value,
                    voucher_id: voucherId,
                    offer_id: offerId,
                    seller_id: sellerId,
                }
            )

            if (response?.offer) {
                dispatch(pushOffer(response.offer))
                dispatch(setSelectedOffer(response.offer))
                return response.offer
            }

            // If we got a response but no offer object, fetch the offer to update state
            try {
                return await dispatch(OffersAction.fetchOfferById(offerId))
            } catch {
                // If fetch fails but request succeeded, just resolve with empty object
                return {} as Offer
            }
        }
    }

    static fetchDeleteVoucherFromCalculation = (
        packages_cart_id: number,
        offerId: number,
        sellerId: number,
        voucherId: number
    ): ThunkAction<Promise<Offer>> => {
        return async (dispatch, getState): Promise<Offer> => {
            const response: {
                offer?: Offer
            } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/offers/delete-voucher/${packages_cart_id}`,
                {
                    offer_id: offerId,
                    seller_id: sellerId,
                    voucher_id: voucherId,
                },
                'delete'
            )

            if (response?.offer) {
                dispatch(pushOffer(response.offer))
                dispatch(setSelectedOffer(response.offer))
                return response.offer
            }

            // If we got a response but no offer object, fetch the offer to update state
            try {
                return await dispatch(OffersAction.fetchOfferById(offerId))
            } catch {
                // If fetch fails but request succeeded, just resolve with empty object
                return {} as Offer
            }
        }
    }

    static setOfferPersonsCount = (payload: {
        packages_cart_id: number
        drivers_count_adult: number
        drivers_count_child: number
    }): ThunkAction<Promise<Offer>> => {
        return async (dispatch, getState): Promise<Offer> => {
            const response: any = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/offers/set-persons/${payload.packages_cart_id}`,
                {
                    drivers_count_adult: payload.drivers_count_adult,
                    drivers_count_child: payload.drivers_count_child,
                },
                'post'
            )

            const state = getState() as RootState
            const selectedOffer = state.offersState.selectedOffer
            const selectedOfferPackagesCart =
                selectedOffer &&
                (selectedOffer.packagesCart?.id === payload.packages_cart_id ||
                    selectedOffer.packages_cart_id === payload.packages_cart_id)
                    ? selectedOffer.packagesCart
                    : null

            const responsePackagesCart: PackagesCart | null =
                response?.data?.packagesCart || response?.packagesCart || null

            if (responsePackagesCart) {
                dispatch(setSelectedOfferPackagesCart(responsePackagesCart))
                dispatch(setSelectedOfferCalculationPackagesCart(responsePackagesCart))
            } else if (selectedOfferPackagesCart) {
                const updatedPackagesCart: PackagesCart = {
                    ...selectedOfferPackagesCart,
                    drivers_count_adult: payload.drivers_count_adult,
                    drivers_count_child: payload.drivers_count_child,
                }

                dispatch(setSelectedOfferPackagesCart(updatedPackagesCart))
                dispatch(setSelectedOfferCalculationPackagesCart(updatedPackagesCart))
            }

            const offer = response?.data?.offer || response?.offer
            if (offer) {
                dispatch(pushOffer(offer))
                dispatch(setSelectedOffer(offer))
                return offer
            }

            if (selectedOffer) {
                return {
                    ...selectedOffer,
                    packagesCart:
                        responsePackagesCart ||
                        (selectedOfferPackagesCart
                            ? {
                                  ...selectedOfferPackagesCart,
                                  drivers_count_adult: payload.drivers_count_adult,
                                  drivers_count_child: payload.drivers_count_child,
                              }
                            : selectedOffer.packagesCart),
                }
            }

            return Promise.reject(response)
        }
    }

    static setOfferDate = (payload: {
        offer_id?: number
        packages_cart_id?: number
        date: string
        time: string
    }): ThunkAction<Promise<Offer | PackagesCart>> => {
        return async (dispatch, getState): Promise<Offer | PackagesCart> => {
            const endpointId = payload.offer_id ?? payload.packages_cart_id
            const endpoint = payload.offer_id ? 'set-date-offer' : 'set-date'

            if (!endpointId) {
                return Promise.reject(new Error('Missing offer_id or packages_cart_id'))
            }

            const response: any = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/offers/${endpoint}/${endpointId}`,
                {
                    date: payload.date,
                    time: payload.time,
                },
                'post'
            )

            const offer: Offer | null = response?.data?.offer || response?.offer
            if (offer) {
                dispatch(pushOffer(offer))
                dispatch(setSelectedOffer(offer))
                return offer
            }

            const packagesCart: PackagesCart | null = response?.data?.packagesCart || response?.packagesCart
            if (packagesCart) {
                dispatch(setSelectedOfferPackagesCart(packagesCart))
                return packagesCart
            }

            return Promise.reject(response)
        }
    }

    static fetchChangeVoucherPrice = (
        packages_cart_id: number,
        packagesCartItemVoucher: PackagesCartItemVoucher,
        type: string,
        value: string | number
    ) => {
        return async (dispatch) => {
            const url = '/admin/offers/change_voucher/'
            try {
                const response: {
                    packagesCart: PackagesCart
                    success: boolean
                } = await ajax.post(url + packages_cart_id, {
                    packages_cart_item_voucher_id: packagesCartItemVoucher.id,
                    packages_cart_id: packagesCartItemVoucher.packages_cart_id,
                    type: type,
                    value: value,
                    api_key: SELLER_API_KEY,
                })
                if (response?.success) {
                    dispatch(setPackageCartToReservation(response.packagesCart))
                    dispatch(setPackageCartToOffer(response.packagesCart))
                    return Promise.resolve(response)
                } else {
                    return Promise.reject('voucher_change_failed')
                }
            } catch (err) {
                consoleLog('voucher_change_failed - reject')
                consoleLog(err)
                return Promise.reject('voucher_change_failed')
            }
        }
    }

    static fetchSellerAddVoucher = (packages_cart_id: number, type: string, value: string | number) => {
        return async (dispatch) => {
            const url = '/admin/offers/add_voucher/'

            try {
                const response: {
                    success: boolean
                    packagesCart: PackagesCart
                } = await ajax.post(
                    url + packages_cart_id,
                    {
                        type: type,
                        value: value,
                        api_key: SELLER_API_KEY,
                    },
                    {}
                )
                if (response?.success) {
                    dispatch(setPackageCartToReservation(response.packagesCart))
                    dispatch(setPackageCartToOffer(response.packagesCart))

                    return Promise.resolve(response)
                } else {
                    return Promise.reject('voucher_change_failed')
                }
            } catch (err) {
                consoleLog('voucher_change_failed - reject')
                consoleLog(err)
                return Promise.reject('voucher_change_failed')
            }
        }
    }

    static setSelectedOffer = (offer: Offer) => setSelectedOffer(offer)
    static clearSelectedOfferReservation = () => clearSelectedOfferReservation()
    static resetSelectedOffer = () => resetSelectedOffer()
}

export default OffersAction
